import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PeriodFilter from '../components/PeriodFilter';
import WeeklyDigestDialog from '../components/WeeklyDigestDialog';
import KPICoverage from '../components/KPICoverage';
import AskRootyButton from '../components/AskRootyButton';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Clock,
  Users,
  ChefHat,
  Target,
  AlertTriangle,
  CheckCircle2,
  Plus,
  Building2,
  Calendar,
  Mail,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Package,
  Percent,
  Receipt,
  Car,
  Star,
  ShoppingCart,
  AlertCircle,
  Upload,
  FileSpreadsheet,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A&W Brand Colors
const AW_COLORS = ['#f97316', '#8b5cf6', '#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#6366f1'];

// KPI Targets from A&W Profit Improvement Program
const KPI_TARGETS = {
  laborPercent: { target: 19, warning: 22, critical: 25, label: 'Labor %' },
  foodCostPercent: { target: 32, warning: 34, critical: 36, label: 'Food Cost %' },
  primeCost: { target: 51, warning: 55, critical: 58, label: 'Prime Cost %' },
  serviceTimeDT: { target: 80, warning: 120, critical: 180, label: 'DT Time' },
  serviceTimeInside: { target: 150, warning: 200, critical: 250, label: 'Inside Time' },
  salesGrowth: { target: 5, warning: 0, critical: -5, label: 'Sales Growth', inverse: true },
  discountPercent: { target: 5, warning: 8, critical: 10, label: 'Discount %' },
  inventoryVariance: { target: 1.5, warning: 2.5, critical: 3.5, label: 'Inv Variance' },
  wastePercent: { target: 1, warning: 2, critical: 3, label: 'Waste %' },
  mysteryShop: { target: 90, warning: 80, critical: 70, label: 'Mystery Shop', inverse: true }
};

export default function KPIDashboard() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [kpiData, setKpiData] = useState([]);
  const [selectedStore, setSelectedStore] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [period, setPeriod] = useState('current_week');
  const [digestOpen, setDigestOpen] = useState(false);
  const [formData, setFormData] = useState({
    store_id: '', week_ending: '', net_sales: '', net_sales_ly: '', transaction_count: '',
    avg_check: '', discount_percent: '', labor_percent: '', food_percent: '', paper_percent: '',
    oil_percent: '', service_time_dt: '', service_time_inside: '', inventory_variance: '',
    beef_variance: '', fry_variance: '', chicken_variance: '', cash_variance: '', cash_over_short: '',
    waste_percent: '', controllable_profit: '', guest_complaints: '', mystery_shop_score: '', notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedStore, period]);

  // Default store users to their own store on first load (managers can switch to any store)
  useEffect(() => {
    if (user?.store_id && selectedStore === 'all' && ['manager', 'employee', 'supervisor'].includes(user.role)) {
      setSelectedStore(user.store_id);
    }
  }, [user?.store_id, user?.role]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesRes, kpiRes] = await Promise.all([
        axios.get(`${API_URL}/stores`),
        axios.get(`${API_URL}/kpi`, {
          params: { period, ...(selectedStore !== 'all' ? { store_id: selectedStore } : {}) }
        })
      ]);
      setStores(storesRes.data);
      setKpiData(kpiRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        net_sales: parseFloat(formData.net_sales) || 0,
        net_sales_ly: parseFloat(formData.net_sales_ly) || 0,
        transaction_count: formData.transaction_count ? parseInt(formData.transaction_count) : null,
        avg_check: formData.avg_check ? parseFloat(formData.avg_check) : null,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        labor_percent: parseFloat(formData.labor_percent) || 0,
        food_percent: parseFloat(formData.food_percent) || 0,
        paper_percent: parseFloat(formData.paper_percent) || 0,
        oil_percent: parseFloat(formData.oil_percent) || 0,
        service_time_dt: parseFloat(formData.service_time_dt) || 0,
        service_time_inside: parseFloat(formData.service_time_inside) || 0,
        inventory_variance: formData.inventory_variance ? parseFloat(formData.inventory_variance) : null,
        beef_variance: formData.beef_variance ? parseFloat(formData.beef_variance) : null,
        fry_variance: formData.fry_variance ? parseFloat(formData.fry_variance) : null,
        chicken_variance: formData.chicken_variance ? parseFloat(formData.chicken_variance) : null,
        cash_variance: parseFloat(formData.cash_variance) || 0,
        cash_over_short: formData.cash_over_short ? parseFloat(formData.cash_over_short) : null,
        waste_percent: formData.waste_percent ? parseFloat(formData.waste_percent) : null,
        controllable_profit: formData.controllable_profit ? parseFloat(formData.controllable_profit) : null,
        guest_complaints: formData.guest_complaints ? parseInt(formData.guest_complaints) : null,
        mystery_shop_score: formData.mystery_shop_score ? parseFloat(formData.mystery_shop_score) : null,
      };
      
      await axios.post(`${API_URL}/kpi`, submitData);
      toast.success('KPI data saved');
      setDialogOpen(false);
      fetchData();
      setFormData({
        store_id: '', week_ending: '', net_sales: '', net_sales_ly: '', transaction_count: '',
        avg_check: '', discount_percent: '', labor_percent: '', food_percent: '', paper_percent: '',
        oil_percent: '', service_time_dt: '', service_time_inside: '', inventory_variance: '',
        beef_variance: '', fry_variance: '', chicken_variance: '', cash_variance: '', cash_over_short: '',
        waste_percent: '', controllable_profit: '', guest_complaints: '', mystery_shop_score: '', notes: ''
      });
    } catch (error) {
      console.error('Error saving KPI:', error);
      toast.error('Failed to save KPI data');
    }
  };

  const handleDownloadReport = async () => {
    try {
      const latestWeek = kpiData[0]?.week_ending;
      const response = await axios.get(`${API_URL}/kpi/report${latestWeek ? `?week_ending=${latestWeek}` : ''}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `kpi_report_${latestWeek || 'all'}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    setCsvImporting(true);
    setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await axios.post(`${API_URL}/kpi/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCsvResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} rows successfully`);
        fetchData();
      }
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} rows failed`);
      }
    } catch (error) {
      toast.error('CSV import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCsvImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/kpi/csv-template`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kpi_import_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  };

  const getKPIStatus = (value, metric) => {
    const config = KPI_TARGETS[metric];
    if (!config || value === null || value === undefined) return 'normal';
    if (config.inverse) {
      if (value >= config.target) return 'good';
      if (value >= config.warning) return 'warning';
      return 'critical';
    } else {
      if (value <= config.target) return 'good';
      if (value <= config.warning) return 'warning';
      return 'critical';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-amber-600 bg-amber-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good': return <CheckCircle2 className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': return <AlertCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  // CRITICAL FIX: filter to latest-week-per-store so we don't aggregate 20 weeks × 9 stores.
  // Role-aware: store managers default to their own store.
  const userStoreId = user?.store_id;
  const isStoreUser = ['manager', 'employee', 'supervisor'].includes(user?.role) && userStoreId;

  // Step 1: collapse to one row per store.
  // Weekly periods → the single week returned. Monthly/YTD → aggregate across weeks (sum sales, average ratios).
  const isMultiWeek = period === 'monthly' || period === 'ytd';
  const AVG_KEYS = ['labor_percent', 'food_percent', 'paper_percent', 'oil_percent', 'total_food_cost', 'prime_cost',
    'service_time_dt', 'service_time_inside', 'discount_percent', 'inventory_variance', 'beef_variance', 'fry_variance',
    'chicken_variance', 'waste_percent', 'mystery_shop_score', 'avg_check', 'sales_growth'];
  const SUM_KEYS = ['net_sales', 'net_sales_ly', 'transaction_count', 'guest_complaints', 'cash_variance'];
  const latestByStore = (() => {
    const map = new Map();
    for (const k of kpiData) {
      const existing = map.get(k.store_id);
      if (!isMultiWeek) {
        if (!existing || (k.week_ending || '') > (existing.week_ending || '')) map.set(k.store_id, k);
        continue;
      }
      if (!existing) {
        map.set(k.store_id, { ...k, id: k.store_id, _weeks: 1, _sums: Object.fromEntries(AVG_KEYS.map(key => [key, [k[key] ?? null]])) });
      } else {
        existing._weeks += 1;
        if ((k.week_ending || '') > (existing.week_ending || '')) existing.week_ending = k.week_ending;
        SUM_KEYS.forEach(key => { existing[key] = (existing[key] || 0) + (k[key] || 0); });
        AVG_KEYS.forEach(key => existing._sums[key].push(k[key] ?? null));
      }
    }
    const rows = Array.from(map.values());
    if (isMultiWeek) {
      rows.forEach(r => {
        AVG_KEYS.forEach(key => {
          const vals = r._sums[key].filter(v => v !== null && v !== undefined);
          r[key] = vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100 : null;
        });
        if (r.net_sales_ly > 0) r.sales_growth = Math.round(((r.net_sales - r.net_sales_ly) / r.net_sales_ly) * 1000) / 10;
        delete r._sums;
      });
    }
    return rows.sort((a, b) => (a.store_name || '').localeCompare(b.store_name || ''));
  })();

  const weeksInPeriod = new Set(kpiData.map(k => k.week_ending)).size;
  const periodLabel = (() => {
    const dates = [...new Set(kpiData.map(k => k.week_ending).filter(Boolean))].sort();
    if (!dates.length) return '';
    if (period === 'monthly') return `${new Date(dates[dates.length - 1] + 'T00:00:00').toLocaleString('en-CA', { month: 'long', year: 'numeric' })} · ${weeksInPeriod} wk${weeksInPeriod === 1 ? '' : 's'}`;
    if (period === 'ytd') return `YTD ${dates[dates.length - 1].slice(0, 4)} · ${weeksInPeriod} wk${weeksInPeriod === 1 ? '' : 's'}`;
    return dates[dates.length - 1];
  })();

  // Step 2: apply store filter (or default-scope for store users)
  const scopedData = (() => {
    let data = latestByStore;
    if (isStoreUser && selectedStore === 'all') {
      data = data.filter(k => k.store_id === userStoreId);
    } else if (selectedStore !== 'all') {
      data = data.filter(k => k.store_id === selectedStore);
    }
    return data;
  })();

  // Calculate aggregated metrics — based on scoped, latest-week-per-store data
  const agg = scopedData.length > 0 ? {
    avgLabor: (scopedData.reduce((s, d) => s + (d.labor_percent || 0), 0) / scopedData.length).toFixed(1),
    avgFoodCost: (scopedData.reduce((s, d) => s + (d.total_food_cost || 0), 0) / scopedData.length).toFixed(1),
    avgPrimeCost: (scopedData.reduce((s, d) => s + (d.prime_cost || 0), 0) / scopedData.length).toFixed(1),
    avgServiceDT: (scopedData.reduce((s, d) => s + (d.service_time_dt || 0), 0) / scopedData.length).toFixed(0),
    avgServiceInside: (scopedData.reduce((s, d) => s + (d.service_time_inside || 0), 0) / scopedData.length).toFixed(0),
    totalSales: scopedData.reduce((s, d) => s + (d.net_sales || 0), 0),
    avgSalesGrowth: (scopedData.reduce((s, d) => s + (d.sales_growth || 0), 0) / scopedData.length).toFixed(1),
    avgInvVar: (scopedData.reduce((s, d) => s + (d.inventory_variance || 0), 0) / scopedData.length).toFixed(2),
    avgWaste: (scopedData.reduce((s, d) => s + (d.waste_percent || 0), 0) / scopedData.length).toFixed(2),
    avgMysteryShop: (scopedData.reduce((s, d) => s + (d.mystery_shop_score || 0), 0) / scopedData.length).toFixed(1),
    totalTransactions: scopedData.reduce((s, d) => s + (d.transaction_count || 0), 0),
    avgCheck: (scopedData.reduce((s, d) => s + (d.avg_check || 0), 0) / scopedData.length).toFixed(2),
    avgComplaints: (scopedData.reduce((s, d) => s + (d.guest_complaints || 0), 0) / scopedData.length).toFixed(1),
    storeCount: scopedData.length,
    weekEnding: periodLabel,
  } : null;

  // Chart data — latest week per store, max 12 bars
  const storeChartData = scopedData.slice(0, 12).map(k => ({
    name: (k.store_name || '').replace('A&W ', '').slice(0, 12),
    fullName: k.store_name,
    labor: k.labor_percent || 0,
    foodCost: k.total_food_cost || 0,
    primeCost: k.prime_cost || 0,
    serviceDT: k.service_time_dt || 0,
    serviceInside: k.service_time_inside || 0,
    salesGrowth: k.sales_growth || 0,
    invVar: k.inventory_variance || 0,
    waste: k.waste_percent || 0,
    mysteryShop: k.mystery_shop_score || 0,
    sales: k.net_sales || 0,
    transactions: k.transaction_count || 0,
    avgCheck: k.avg_check || 0,
    complaints: k.guest_complaints || 0
  }));

  // Sales comparison data
  const salesData = scopedData.slice(0, 12).map(k => ({
    name: (k.store_name || '').replace('A&W ', '').slice(0, 12),
    thisYear: k.net_sales || 0,
    lastYear: k.net_sales_ly || 0,
    growth: k.sales_growth || 0
  }));

  // Inventory variance by item
  const invVarianceData = scopedData.slice(0, 12).map(k => ({
    name: (k.store_name || '').replace('A&W ', '').slice(0, 12),
    beef: k.beef_variance || 0,
    fry: k.fry_variance || 0,
    chicken: k.chicken_variance || 0,
    total: k.inventory_variance || 0
  }));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading KPI data...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="kpi-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Target className="w-8 h-8" />
              Weekly KPI Dashboard
            </h1>
            <p className="text-orange-100">Track sales, costs, service times, inventory, and profit metrics</p>
          </div>
          {user?.role === 'owner' && (
            <div className="flex gap-2">
              <Button onClick={async () => {
                try {
                  const res = await axios.post(`${API_URL}/kpi/check-thresholds`);
                  if (res.data.alerts_sent > 0) {
                    toast.warning(`${res.data.alerts_sent} threshold(s) exceeded! Email alert sent.`);
                  } else {
                    toast.success('All KPIs within acceptable limits');
                  }
                } catch { toast.error('Failed to check thresholds'); }
              }} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-sm" data-testid="check-thresholds-btn">
                <AlertTriangle className="w-4 h-4 mr-2" />Check Thresholds
              </Button>
              <Button onClick={() => setDigestOpen(true)} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-sm" data-testid="weekly-digest-btn">
                <Mail className="w-4 h-4 mr-2" />Weekly Digest
              </Button>
              <Button onClick={handleDownloadReport} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-sm">
                <Download className="w-4 h-4 mr-2" />Download Report
              </Button>
              <Button onClick={() => { setCsvFile(null); setCsvResult(null); setCsvDialogOpen(true); }} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-sm" data-testid="csv-import-btn">
                <Upload className="w-4 h-4 mr-2" />Import CSV
              </Button>
              <Button onClick={() => setDialogOpen(true)} className="bg-white text-orange-600 hover:bg-orange-50 rounded-sm font-semibold">
                <Plus className="w-4 h-4 mr-2" />Add Weekly Data
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Data routine: coverage matrix + reminders (owners) */}
      {['owner', 'area_manager'].includes(user?.role) && (
        <KPICoverage onAddWeek={(storeId, week) => { setFormData(f => ({ ...f, store_id: storeId, week_ending: week })); setDialogOpen(true); }} />
      )}

      {/* Period filter + Ask Rooty */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodFilter value={period} onChange={setPeriod} />
        <WeeklyDigestDialog open={digestOpen} onOpenChange={setDigestOpen} />
        <AskRootyButton
          label="Ask Rooty about KPIs"
          dataTestId="ask-rooty-kpi"
          contextPrompt={`Analyze our A&W KPI data for the period: ${period.replace('_', ' ')}. Use the latest available week. Highlight: (1) best-performing store on prime cost, (2) any store breaching A&W thresholds (labour > 25%, food cost > 35%, DT > 120s), (3) one actionable focus area for the operations team. Be specific with store names and numbers.`}
        />
      </div>

      {/* Store Filter */}
      {stores.length > 0 && kpiData.length > 0 && (
        <Select value={selectedStore} onValueChange={setSelectedStore}>
          <SelectTrigger className="w-48 rounded-sm border-slate-200">
            <Building2 className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="All Stores" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            {stores.map((store) => (
              <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Empty State for a period with no rows (data exists elsewhere) */}
      {kpiData.length === 0 && period !== 'current_week' && !loading && (
        <Card className="bg-white border border-slate-200 rounded-lg" data-testid="kpi-period-empty-state">
          <CardContent className="py-12 text-center">
            <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-900 mb-1">No KPI data for this period</h3>
            <p className="text-sm text-slate-500 mb-4">Nothing has been uploaded for “{period.replace('_', ' ')}” yet. Try another period or import more weeks.</p>
            <Button variant="outline" onClick={() => setPeriod('current_week')} className="rounded-sm border-slate-200" data-testid="kpi-period-reset-btn">Back to Current Week</Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State when no KPI data */}
      {kpiData.length === 0 && period === 'current_week' && (
        <Card className="bg-white border border-slate-200 rounded-lg" data-testid="kpi-empty-state">
          <CardContent className="py-16 text-center">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <Target className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>No Weekly KPI Data Yet</h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Start tracking your store performance by adding your first week of KPI data. Targets will be applied automatically based on A&W Profit Improvement benchmarks.
            </p>
            {user?.role === 'owner' && (
              <div className="flex gap-3 justify-center">
                <Button onClick={() => setDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-semibold" data-testid="empty-add-kpi-btn">
                  <Plus className="w-4 h-4 mr-2" />Add First Weekly Data
                </Button>
                <Button onClick={() => { setCsvFile(null); setCsvResult(null); setCsvDialogOpen(true); }} variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50 rounded-sm font-semibold" data-testid="empty-csv-import-btn">
                  <Upload className="w-4 h-4 mr-2" />Import from CSV
                </Button>
              </div>
            )}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              <div className="bg-slate-50 rounded-lg p-3 text-left">
                <p className="text-xs text-slate-400 mb-1">Labor Target</p>
                <p className="text-sm font-bold text-slate-700">19%</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-left">
                <p className="text-xs text-slate-400 mb-1">Food Cost Target</p>
                <p className="text-sm font-bold text-slate-700">32%</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-left">
                <p className="text-xs text-slate-400 mb-1">DT Time Target</p>
                <p className="text-sm font-bold text-slate-700">80s</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-left">
                <p className="text-xs text-slate-400 mb-1">Mystery Shop Target</p>
                <p className="text-sm font-bold text-slate-700">90+</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Summary Cards - Row 1 - Only show when data exists */}
      {kpiData.length > 0 && (<>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{agg?.storeCount || 0} store{agg?.storeCount === 1 ? '' : 's'}{agg?.weekEnding ? ` · ${agg.weekEnding}` : ''}</span>
            </div>
            <p className="text-xl font-bold text-slate-900">${agg?.totalSales?.toLocaleString() || '--'}</p>
            <p className="text-xs text-slate-500">Total Sales</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${parseFloat(agg?.avgSalesGrowth || 0) >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>vs LY</span>
            </div>
            <p className="text-xl font-bold text-slate-900 flex items-center gap-1">
              {agg?.avgSalesGrowth || '--'}%
              {parseFloat(agg?.avgSalesGrowth || 0) >= 0 ? <ArrowUpRight className="w-4 h-4 text-green-500" /> : <ArrowDownRight className="w-4 h-4 text-red-500" />}
            </p>
            <p className="text-xs text-slate-500">Avg Growth</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-purple-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgLabor || 0), 'laborPercent'))}`}>T: 19%</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgLabor || '--'}%</p>
            <p className="text-xs text-slate-500">Labor Cost</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <ChefHat className="w-5 h-5 text-orange-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgFoodCost || 0), 'foodCostPercent'))}`}>T: 32%</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgFoodCost || '--'}%</p>
            <p className="text-xs text-slate-500">Food Cost</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Receipt className="w-5 h-5 text-indigo-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgPrimeCost || 0), 'primeCost'))}`}>T: 51%</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgPrimeCost || '--'}%</p>
            <p className="text-xs text-slate-500">Prime Cost</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgMysteryShop || 0), 'mysteryShop'))}`}>T: 90</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgMysteryShop || '--'}</p>
            <p className="text-xs text-slate-500">Mystery Shop</p>
          </CardContent>
        </Card>
      </div>

      {/* KPI Summary Cards - Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Car className="w-5 h-5 text-blue-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgServiceDT || 0), 'serviceTimeDT'))}`}>T: 80s</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgServiceDT || '--'}s</p>
            <p className="text-xs text-slate-500">Drive-Thru</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-cyan-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgServiceInside || 0), 'serviceTimeInside'))}`}>T: 150s</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgServiceInside || '--'}s</p>
            <p className="text-xs text-slate-500">Inside Service</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-5 h-5 text-teal-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgInvVar || 0), 'inventoryVariance'))}`}>T: &lt;1.5%</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgInvVar || '--'}%</p>
            <p className="text-xs text-slate-500">Inv. Variance</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Percent className="w-5 h-5 text-red-500" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(getKPIStatus(parseFloat(agg?.avgWaste || 0), 'wastePercent'))}`}>T: &lt;1%</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.avgWaste || '--'}%</p>
            <p className="text-xs text-slate-500">Waste</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <ShoppingCart className="w-5 h-5 text-emerald-500" />
            </div>
            <p className="text-xl font-bold text-slate-900">{agg?.totalTransactions?.toLocaleString() || '--'}</p>
            <p className="text-xs text-slate-500">Transactions</p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-xl font-bold text-slate-900">${agg?.avgCheck || '--'}</p>
            <p className="text-xs text-slate-500">Avg Check</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 - Sales Comparison & Cost Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <DollarSign className="w-5 h-5 text-green-600" />
              Sales by Store (TY vs LY)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                  <Legend />
                  <Bar dataKey="thisYear" name="This Year" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lastYear" name="Last Year" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Users className="w-5 h-5 text-purple-600" />
              Labor & Food Cost by Store
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={storeChartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 40]} />
                  <Tooltip formatter={(value) => [`${value}%`, '']} />
                  <Legend />
                  <Bar dataKey="labor" name="Labor %" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="foodCost" name="Food Cost %" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
              <span>Target Labor: 19%</span>
              <span>Target Food: 32%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 - Service Times & Inventory */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Clock className="w-5 h-5 text-blue-600" />
              Service Times by Store
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={storeChartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${v}s`} domain={[0, 200]} />
                  <Tooltip formatter={(value) => [`${value}s`, '']} />
                  <Legend />
                  <Bar dataKey="serviceDT" name="Drive-Thru" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="serviceInside" name="Inside" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
              <span>DT Target: 80s</span>
              <span>Inside Target: 150s</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Package className="w-5 h-5 text-teal-600" />
              Inventory Variance by Item
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={invVarianceData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 3]} />
                  <Tooltip formatter={(value) => [`${value}%`, '']} />
                  <Legend />
                  <Bar dataKey="beef" name="Beef" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="fry" name="Fries" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="chicken" name="Chicken" fill="#84cc16" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
              <span>Target: &lt;1.5%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 - Performance & Quality */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Star className="w-5 h-5 text-yellow-600" />
              Mystery Shop Scores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={storeChartData} layout="vertical">
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(value) => [`${value}`, 'Score']} />
                  <Bar dataKey="mysteryShop" name="Score" fill="#f59e0b" radius={[0, 4, 4, 0]}>
                    {storeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.mysteryShop >= 90 ? '#10b981' : entry.mysteryShop >= 80 ? '#f59e0b' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span>90+</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded"></span>80-89</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded"></span>&lt;80</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Sales Growth by Store
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={storeChartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[-10, 15]} />
                  <Tooltip formatter={(value) => [`${value}%`, 'Growth']} />
                  <Bar dataKey="salesGrowth" name="Sales Growth" radius={[4, 4, 0, 0]}>
                    {storeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.salesGrowth >= 0 ? '#10b981' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store Performance Table */}
      <Card className="bg-white border border-slate-200 rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Building2 className="w-5 h-5 text-orange-600" />
            Store Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Store</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Sales</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Growth</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Labor</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Food</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Prime</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">DT</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Inside</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Inv Var</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Shop</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-500 uppercase tracking-wide text-xs">Status</th>
                </tr>
              </thead>
              <tbody>
                {scopedData.map((store) => {
                  const laborS = getKPIStatus(store.labor_percent, 'laborPercent');
                  const foodS = getKPIStatus(store.total_food_cost, 'foodCostPercent');
                  const primeS = getKPIStatus(store.prime_cost, 'primeCost');
                  const dtS = getKPIStatus(store.service_time_dt, 'serviceTimeDT');
                  const insideS = getKPIStatus(store.service_time_inside, 'serviceTimeInside');
                  const invS = getKPIStatus(store.inventory_variance, 'inventoryVariance');
                  const shopS = getKPIStatus(store.mystery_shop_score, 'mysteryShop');
                  const statuses = [laborS, foodS, primeS, dtS, insideS, invS, shopS];
                  const overallStatus = statuses.includes('critical') ? 'critical' : statuses.includes('warning') ? 'warning' : 'good';
                  
                  return (
                    <tr key={store.id || store.store_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`kpi-row-${store.store_id}`}>
                      <td className="py-3 px-3 font-medium text-slate-900">{store.store_name}</td>
                      <td className="py-3 px-3 text-right">${store.net_sales?.toLocaleString()}</td>
                      <td className="py-3 px-3 text-right">
                        <span className={`flex items-center justify-end gap-1 ${store.sales_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {store.sales_growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {store.sales_growth}%
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(laborS)}`}>{store.labor_percent}%</span></td>
                      <td className="py-3 px-3 text-right"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(foodS)}`}>{store.total_food_cost}%</span></td>
                      <td className="py-3 px-3 text-right"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(primeS)}`}>{store.prime_cost}%</span></td>
                      <td className="py-3 px-3 text-right"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(dtS)}`}>{store.service_time_dt}s</span></td>
                      <td className="py-3 px-3 text-right"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(insideS)}`}>{store.service_time_inside}s</span></td>
                      <td className="py-3 px-3 text-right"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(invS)}`}>{store.inventory_variance}%</span></td>
                      <td className="py-3 px-3 text-right"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(shopS)}`}>{store.mystery_shop_score}</span></td>
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(overallStatus)}`}>
                          {getStatusIcon(overallStatus)}
                          {overallStatus === 'good' ? 'On Track' : overallStatus === 'warning' ? 'Focus' : 'Critical'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      </>)}

      {/* Add KPI Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add Weekly KPI Data</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>Store *</Label>
                <Select value={formData.store_id} onValueChange={(v) => setFormData({...formData, store_id: v})}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select store" /></SelectTrigger>
                  <SelectContent>
                    {stores.map((store) => (<SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Week Ending *</Label>
                <Input type="date" value={formData.week_ending} onChange={(e) => setFormData({...formData, week_ending: e.target.value})} className="rounded-sm" required />
              </div>
            </div>

            <Tabs defaultValue="sales" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-4">
                <TabsTrigger value="sales">Sales</TabsTrigger>
                <TabsTrigger value="costs">Costs</TabsTrigger>
                <TabsTrigger value="service">Service</TabsTrigger>
                <TabsTrigger value="inventory">Inventory</TabsTrigger>
                <TabsTrigger value="other">Other</TabsTrigger>
              </TabsList>

              <TabsContent value="sales" className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Net Sales ($) *</Label><Input type="number" step="0.01" value={formData.net_sales} onChange={(e) => setFormData({...formData, net_sales: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Net Sales LY ($) *</Label><Input type="number" step="0.01" value={formData.net_sales_ly} onChange={(e) => setFormData({...formData, net_sales_ly: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Transactions</Label><Input type="number" value={formData.transaction_count} onChange={(e) => setFormData({...formData, transaction_count: e.target.value})} className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Avg Check ($)</Label><Input type="number" step="0.01" value={formData.avg_check} onChange={(e) => setFormData({...formData, avg_check: e.target.value})} className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Discount %</Label><Input type="number" step="0.01" value={formData.discount_percent} onChange={(e) => setFormData({...formData, discount_percent: e.target.value})} className="rounded-sm" /></div>
              </TabsContent>

              <TabsContent value="costs" className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Labor % (T: 19%)*</Label><Input type="number" step="0.1" value={formData.labor_percent} onChange={(e) => setFormData({...formData, labor_percent: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Food %*</Label><Input type="number" step="0.1" value={formData.food_percent} onChange={(e) => setFormData({...formData, food_percent: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Paper %*</Label><Input type="number" step="0.1" value={formData.paper_percent} onChange={(e) => setFormData({...formData, paper_percent: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Oil %*</Label><Input type="number" step="0.1" value={formData.oil_percent} onChange={(e) => setFormData({...formData, oil_percent: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Waste %</Label><Input type="number" step="0.1" value={formData.waste_percent} onChange={(e) => setFormData({...formData, waste_percent: e.target.value})} className="rounded-sm" /></div>
              </TabsContent>

              <TabsContent value="service" className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Drive-Thru (s) T: 80s *</Label><Input type="number" step="1" value={formData.service_time_dt} onChange={(e) => setFormData({...formData, service_time_dt: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Inside (s) T: 150s *</Label><Input type="number" step="1" value={formData.service_time_inside} onChange={(e) => setFormData({...formData, service_time_inside: e.target.value})} className="rounded-sm" required /></div>
              </TabsContent>

              <TabsContent value="inventory" className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Total Inv Variance %</Label><Input type="number" step="0.01" value={formData.inventory_variance} onChange={(e) => setFormData({...formData, inventory_variance: e.target.value})} className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Beef Variance %</Label><Input type="number" step="0.01" value={formData.beef_variance} onChange={(e) => setFormData({...formData, beef_variance: e.target.value})} className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Fry Variance %</Label><Input type="number" step="0.01" value={formData.fry_variance} onChange={(e) => setFormData({...formData, fry_variance: e.target.value})} className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Chicken Variance %</Label><Input type="number" step="0.01" value={formData.chicken_variance} onChange={(e) => setFormData({...formData, chicken_variance: e.target.value})} className="rounded-sm" /></div>
              </TabsContent>

              <TabsContent value="other" className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Cash Variance ($)*</Label><Input type="number" step="0.01" value={formData.cash_variance} onChange={(e) => setFormData({...formData, cash_variance: e.target.value})} className="rounded-sm" required /></div>
                <div className="space-y-2"><Label>Guest Complaints</Label><Input type="number" value={formData.guest_complaints} onChange={(e) => setFormData({...formData, guest_complaints: e.target.value})} className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Mystery Shop Score</Label><Input type="number" step="0.1" value={formData.mystery_shop_score} onChange={(e) => setFormData({...formData, mystery_shop_score: e.target.value})} className="rounded-sm" /></div>
                <div className="space-y-2 col-span-2"><Label>Notes</Label><Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="rounded-sm" /></div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm">Save KPI Data</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <FileSpreadsheet className="w-5 h-5 text-orange-500" />
              Import Weekly KPI Data from CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template Download */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 mb-2 font-medium">Need a template?</p>
              <p className="text-xs text-amber-600 mb-3">Download a pre-formatted CSV with your store names and all the right columns.</p>
              <Button onClick={handleDownloadTemplate} variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="download-csv-template-btn">
                <Download className="w-3 h-3 mr-1.5" />Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Upload CSV File</Label>
              {!csvFile ? (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors" data-testid="csv-dropzone">
                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-500">Click to select CSV file</span>
                  <span className="text-xs text-slate-400 mt-1">Supports .csv files</span>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => { setCsvFile(e.target.files[0]); setCsvResult(null); }}
                    data-testid="csv-file-input"
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{csvFile.name}</p>
                      <p className="text-xs text-slate-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button onClick={() => { setCsvFile(null); setCsvResult(null); }} className="p-1 hover:bg-slate-200 rounded" data-testid="csv-remove-file">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              )}
            </div>

            {/* Import Result */}
            {csvResult && (
              <div className={`border rounded-lg p-4 ${csvResult.imported > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="csv-import-result">
                <div className="flex items-center gap-2 mb-2">
                  {csvResult.imported > 0 ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-medium text-sm">
                    {csvResult.imported} imported{csvResult.failed > 0 ? `, ${csvResult.failed} failed` : ''}
                  </span>
                </div>
                {csvResult.preview && csvResult.preview.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {csvResult.preview.map((row, i) => (
                      <div key={i} className="text-xs text-slate-600 flex justify-between">
                        <span>{row.store}</span>
                        <span>{row.week}</span>
                        <span className="font-medium">${row.sales?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {csvResult.errors && csvResult.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {csvResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-600">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setCsvDialogOpen(false)} className="rounded-sm">
                {csvResult?.imported > 0 ? 'Done' : 'Cancel'}
              </Button>
              {!csvResult?.imported && (
                <Button
                  onClick={handleCsvImport}
                  disabled={!csvFile || csvImporting}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                  data-testid="csv-import-submit-btn"
                >
                  {csvImporting ? 'Importing...' : 'Import Data'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
